import React, { Component } from "reactn";
import { $, _ } from "../../vendor";

export function RowGrid(props) {
    props = {
        gap: 20,
        margin: "",
        ...props,
    };
    let style = {
        rowGap: props.gap,
        margin: props.margin,
        ...props.style
    };

    return (
        <div
            className="row-grid"
            disabled={props.disabled}
            style={style}
        >
            {props.children}
        </div>
    );
}

export function ColGrid(props) {
    props = {
        gap: 20,
        ...props,
    };
    let style = {
        columnGap: props.gap,
        ...props.style
    };

    return (
        <div
            className="col-grid"
            disabled={props.disabled}
            style={style}
        >
            {props.children}
        </div>
    );
}

/**
 * @typedef {object} BoxProps
 * @property {boolean} [fillWidth] - If true, the box will take 100% of the available width.
 * @property {boolean} [fillHeight] - If true, the box will take 100% of the available height.
 * @property {boolean} [fill] - If true, the box will take 100% of both width and height.
 * @property {number | string} [width] - Sets the width of the box.
 * @property {number | string} [height] - Sets the height of the box.
 * @property {number | string} [maxHeight] - Sets the max height of the box.
 * @property {number | string} [minHeight] - Sets the min height of the box.
 * @property {number | string} [maxWidth] - Sets the max width of the box.
 * @property {number | string} [minWidth] - Sets the min width of the box.
 * @property {boolean} [center] - If true, centers the content horizontally and vertically (uses flexbox properties).
 * @property {number | string} [padding] - Sets the padding of the box.
 * @property {number} [scale] - Applies a transform scale to the box.
 * @property {string} [id] - The HTML id attribute for the box element.
 * @property {React.CSSProperties} [style] - Custom CSS styles for the box container.
 * @property {React.ReactNode} [children] - The content to be rendered inside the box.
 * // Additional props consumed by Box's internal logic (omitted from the render's div spread but handled by getStyles or similar)
 * @property {boolean} [column] - Used with FlexBox (though defined in BoxProps for convenience/inheritance).
 * @property {boolean} [vertical] - Alias for column. Used with FlexBox.
 * @property {boolean} [wrap] - Used with FlexBox.
 * @property {'start' | 'left' | 'center' | 'right' | 'between' | 'around' | 'evenly'} [horizontalAlign] - Used with FlexBox.
 * @property {'start' | 'top' | 'middle' | 'center' | 'bottom'} [verticalAlign] - Used with FlexBox.
 * @property {boolean} [left] - Used with FlexBox for alignment.
 * @property {boolean} [right] - Used with FlexBox for alignment.
 * @property {boolean} [top] - Used with FlexBox for alignment.
 * @property {boolean} [bottom] - Used with FlexBox for alignment.
 * @property {boolean} [middle] - Used with FlexBox for alignment.
 * @property {boolean} [between] - Used with FlexBox for justification.
 * @property {number | string} [gap] - Used with FlexBox/GridBox.
 * @property {number | string} [verticalPadding] - Not explicitly used in getStyles/omitted props, but potentially handled elsewhere or intended. Included based on FlexBoxProps.
 * @property {boolean} [showMore] - Not explicitly used in getStyles/omitted props, but potentially handled elsewhere or intended. Included based on FlexBoxProps.
 * @property {number} [rows] - Used with GridBox.
 * @property {number} [columns] - Used with GridBox.
 * @property {string} [template] - Used with GridBox for grid-template-columns/rows.
 */

/**
 * A base component for layout boxes, handling common styling and size props.
 * @augments {React.Component<BoxProps>}
 */
export class Box extends Component {
    constructor(props) {
        super(props);

        this.ref = React.createRef();
    }

    get defaultStyles() {
        return {};
    }

    getStyles() {
        const styles = { ...this.props.style };

        if (this.props.fillWidth || this.props.fill) {
            styles.width = "100%";
        }

        if (this.props.fillHeight || this.props.fill) {
            styles.height = "100%";
        }

        if (this.props.width) {
            styles.width = this.props.width;
        }

        if (this.props.height) {
            styles.height = this.props.height;
        }

        if (this.props.maxHeight) {
            styles.maxHeight = this.props.maxHeight;
        }

        if (this.props.minHeight) {
            styles.minHeight = this.props.minHeight;
        }

        if (this.props.maxWidth) {
            styles.maxWidth = this.props.maxWidth;
        }

        if (this.props.minWidth) {
            styles.minWidth = this.props.minWidth;
        }

        if (this.props.center) {
            styles.alignItems = "center";
            styles.justifyContent = "center";
        }

        if (this.props.padding) {
            styles.padding = this.props.padding;
        }

        if (this.props.scale) {
            styles.transform = `scale(${this.props.scale})`;
        }

        return styles;
    }

    render() {
        const {
            id,
            style,
            children,
        } = this.props;

        const styles = this.getStyles();

        const componentProps = _.omit(this.props, [
            "fill",
            "fillWidth",
            "fillHeight",
            "width",
            "height",
            "column",
            "vertical",
            "center",
            "wrap",
            "horizontalAlign",
            "verticalAlign",
            "left",
            "right",
            "top",
            "bottom",
            "center",
            "middle",
            "rows",
            "columns",
            "template",
            "between",
            "verticalPadding",
            "showMore"
        ]);

        return (
            <div id={id}
                key={id}
                {...componentProps}
                style={{
                    ...styles,
                    ...style,
                }}
                ref={this.ref}
            >
                {children}
            </div>
        );
    }
}

export class AbsoluteBox extends Box {
    getStyles() {
        let props = this.props;
        let styles = super.getStyles();

        styles.position = "absolute";
        styles.left = props.left;
        styles.right = props.right;
        styles.top = props.top;
        styles.bottom = props.bottom;
        styles.width = props.width;
        styles.height = props.height;

        if (props.point) {
            styles.left = props.point.x;
            styles.top = props.point.y;
        }

        if (props.centerTransform || props.center) {
            styles.transform = "translate(-50%, -50%)";
        }

        if (props.transition) {
            styles.transition = props.transition;
        }

        return styles;
    }
}

/**
 * @typedef {object} FlexBoxProps
 * // Inherits all props from BoxProps
 * @augments {BoxProps}
 * @property {number | string} [gap] - The gap between flex items.
 * @property {boolean} [column] - If true, sets flex direction to column.
 * @property {boolean} [vertical] - Alias for column.
 * @property {boolean} [center] - Centers content horizontally and vertically.
 * @property {boolean} [wrap] - Allows flex items to wrap.
 * @property {'start' | 'left' | 'center' | 'right' | 'between' | 'around' | 'evenly'} [horizontalAlign] - Horizontal alignment of flex items.
 * @property {'start' | 'top' | 'middle' | 'center' | 'bottom'} [verticalAlign] - Vertical alignment of flex items.
 * @property {boolean} [left] - Aligns content to the left (depends on flex direction).
 * @property {boolean} [right] - Aligns content to the right (depends on flex direction).
 * @property {boolean} [top] - Aligns content to the top (depends on flex direction).
 * @property {boolean} [bottom] - Aligns content to the bottom (depends on flex direction).
 * @property {boolean} [middle] - Aligns content to the middle (depends on flex direction).
 * @property {boolean} [between] - Justifies content with space between.
 */

/**
 * A flexible Box component using CSS Flexbox.
 * @augments {React.Component<FlexBoxProps>}
 */
export class FlexBox extends Box {
    getStyles() {
        let props = this.props;
        let styles = super.getStyles();

        styles.display = styles.display || "flex";

        if (props.gap) {
            styles.gap = props.gap;
        }

        if (props.column || props.vertical) {
            styles.flexDirection = "column";
        }

        if (props.center) {
            styles.alignItems = "center";
            styles.justifyContent = "center";
        }

        if (props.wrap) {
            styles.flexWrap = "wrap";
        }

        if (props.grow) {
            styles.flexGrow = 1;
        }
        if (props.shrink) {
            styles.flexShrink = 1;
        }

        switch (props.horizontalAlign || "center") {
            case "start":
                styles.justifyContent = "start";
                break;
            case "left":
                styles.justifyContent = "flex-start";
                break;
            case "center":
                styles.justifyContent = "center";
                break;
            case "right":
                styles.justifyContent = "flex-end";
                break;
            case "between":
                styles.justifyContent = "space-between";
                break;
            case "around":
                styles.justifyContent = "space-around";
                break;
            case "evenly":
                styles.justifyContent = "space-evenly";
                break;
        }

        switch (props.verticalAlign || "middle") {
            case "start":
                styles.alignItems = "start";
                break;
            case "top":
                styles.alignItems = "flex-start";
                break;
            case "middle":
            case "center":
                styles.alignItems = "center";
                break;
            case "bottom":
                styles.alignItems = "flex-end";
                break;
        }

        if (styles.flexDirection == "column") {
            if (props.left) {
                styles.alignItems = "flex-start";
            }
            if (props.right) {
                styles.alignItems = "flex-end";
            }
            if (props.top) {
                styles.justifyContent = "flex-start";
            }
            if (props.bottom) {
                styles.justifyContent = "flex-end";
            }
            if (props.middle) {
                styles.justifyContent = "center";
            }
        } else {
            if (props.left) {
                styles.justifyContent = "flex-start";
            }
            if (props.right) {
                styles.justifyContent = "flex-end";
            }
            if (props.top) {
                styles.alignItems = "flex-start";
            }
            if (props.bottom) {
                styles.alignItems = "flex-end";
            }
            if (props.middle) {
                styles.alignItems = "center";
            }
        }
        if (props.between) {
            styles.justifyContent = "space-between";
        }

        return styles;
    }
}

export class GridBox extends Box {
    getStyles() {
        let {
            columns = false,
            gap = 20,
            template,
        } = this.props;
        let styles = super.getStyles();

        styles.display = "grid";
        if (columns) {
            styles.gridAutoFlow = "column";
            styles.gridAutoColumns = "max-content";
            if (template) {
                styles.gridTemplateRows = template;
            }
        } else {
            styles.gridAutoFlow = "row";
            styles.gridAutoRows = "max-content";
            if (template) {
                styles.gridTemplateColumns = template;
            }
        }
        styles.gridGap = gap;

        return styles;
    }
}

export class ColumnGridBox extends Box {
    getStyles() {
        let {
            columns,
            gap = 10,
        } = this.props;

        let styles = super.getStyles();

        styles.display = "grid";
        styles.gridAutoFlow = "column";
        styles.gridGap = gap;

        if (columns) {
            // styles.gridTemplateColumns = `repeat(${columns}, max-content)`;
            styles.gridTemplateColumns = `repeat(${columns}, ${100 / columns}%)`;
        }

        return styles;
    }
}

export function FillDiv(props) {
    let style = {
        width: "100%",
        height: "100%",
        ...props.style
    };

    return (
        <div
            id={props.id}
            disabled={props.disabled}
            style={style}
        >
            {props.children}
        </div>
    );
}

/**
 * @typedef {object} ScrollBoxProps
 * // Inherits all props from BoxProps
 * @augments {BoxProps}
 * // ScrollBox does not define additional props beyond those handled by Box or standard div elements.
 */

/**
 * A Box component with enforced vertical scrolling and a method to scroll to a specific element.
 * @augments {React.Component<ScrollBoxProps>}
 */
export class ScrollBox extends Box {
    /**
     * Scrolls the box to the element matching the given CSS selector.
     * @param {string} selector - The CSS selector of the element to scroll to.
     * @param {number} [duration=400] - The duration of the scroll animation in milliseconds.
     */
    scrollTo(selector, duration = 400, offset = 0) {
        let $el = $(this.ref.current);
        let $target = $(this.ref.current).find(selector);

        if ($target.length) {
            let scroll = $target.offset().top - $el.offset().top + offset;
            $el.animate({ scrollTop: $el.scrollTop() + scroll }, duration);
        }
    }

    getStyles() {
        let styles = super.getStyles();

        styles.overflowY = "auto";
        styles.overflowX = "hidden";

        return styles;
    }
}
